/**
 * Remove todos os caracteres não numéricos de uma string.
 * @param value A string a ser limpa.
 * @returns A string contendo apenas dígitos.
 */
export const onlyDigits = (value: string): string => {
	if (!value) {
		return "";
	}
	return value.replace(/\D/g, "");
};

const CPF_GROUP_REGEX = /(\d{3})(\d)/;
const CPF_FINAL_REGEX = /(\d{3})(\d{1,2})$/;
const CEP_REGEX = /^(\d{5})(\d+)/;

/**
 * Aplica a máscara de CPF (999.999.999-99) a uma string de dígitos.
 * @param value A string de dígitos a ser mascarada.
 * @returns A string formatada como CPF.
 */
export const maskCPF = (value: string): string => {
	const cleanedValue = onlyDigits(value);
	if (!cleanedValue) {
		return "";
	}
	return cleanedValue
		.replace(CPF_GROUP_REGEX, "$1.$2")
		.replace(CPF_GROUP_REGEX, "$1.$2")
		.replace(CPF_FINAL_REGEX, "$1-$2");
};

/**
 * Aplica a máscara de CEP (00000-000) a uma string de dígitos.
 * @param value A string de dígitos a ser mascarada.
 * @returns A string formatada como CEP.
 */
export const maskCEP = (value: string): string => {
	const cleanedValue = value.replace(/\D/g, ""); // remove tudo que não for dígito
	if (!cleanedValue) {
		return "";
	}
	if (cleanedValue.length <= 5) {
		return cleanedValue;
	}
	return cleanedValue
		.replace(CEP_REGEX, "$1-$2") // divide após os primeiros 5 dígitos
		.slice(0, 9); // garante que não ultrapasse “00000-000”
};

/**
 * Aplica a máscara de telefone brasileiro.
 * Suporta formatação progressiva e números de 10 ou 11 dígitos.
 * Exemplos:
 * - 10 dígitos: (62) 9999-9999
 * - 11 dígitos: (62) 99999-9999
 */
export const maskPhone = (value: string): string => {
	const digits = onlyDigits(value).slice(0, 11);
	if (!digits) {
		return "";
	}

	if (digits.length <= 2) {
		return `(${digits}`;
	}

	const ddd = digits.slice(0, 2);
	const rest = digits.slice(2);
	// Para 11 dígitos, usamos 5 antes do hífen. Para 10, usamos 4.
	const useNineDigitPattern = rest.length > 8; // indica total de 11 dígitos
	const firstBlockLength = useNineDigitPattern ? 5 : 4;

	if (rest.length <= firstBlockLength) {
		return `(${ddd}) ${rest}`;
	}

	const first = rest.slice(0, firstBlockLength);
	const second = rest.slice(firstBlockLength, 9); // limita ao padrão final
	return `(${ddd}) ${first}-${second}`;
};

/**
 * Aplica a máscara de hodômetro, formatando números com pontos a cada 3 dígitos da direita para a esquerda.
 * Exemplos: 1 → "1", 11 → "11", 111 → "111", 1111 → "1.111", 11111 → "11.111", 111111 → "111.111"
 * Garante que sempre use ponto (.) e nunca vírgula (,), mesmo se o valor de entrada contiver vírgulas.
 * @param value A string de dígitos a ser mascarada.
 * @returns A string formatada como hodômetro com pontos.
 */
export const maskOdometer = (value: string): string => {
	// Remove tudo que não for dígito e substitui vírgulas por nada (caso apareçam)
	const cleanedValue = onlyDigits(value.replace(/,/g, ""));
	if (!cleanedValue) {
		return "";
	}

	// Processa da direita para a esquerda, adicionando pontos a cada 3 dígitos
	// Usa uma regex que funciona da direita para a esquerda usando lookahead
	return cleanedValue.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

const FUEL_LITERS_DECIMALS = 3;
const FUEL_LITERS_MIN_INTEGER_DIGITS = 1;

/**
 * Formata quantidade em litros com 3 casas decimais (vírgula) da direita para a esquerda.
 * Exemplos:
 * - "1" → "0,001"
 * - "12" → "0,012"
 * - "1234" → "1,234"
 * - "12345" → "12,345"
 */
export const maskFuelLiters = (value: string): string => {
	const digits = onlyDigits(value);
	if (!digits) {
		return "";
	}

	const integerDigits = digits.slice(0, -FUEL_LITERS_DECIMALS);
	const decimalsDigits = digits
		.slice(-FUEL_LITERS_DECIMALS)
		.padStart(FUEL_LITERS_DECIMALS, "0");

	const integerPart = (integerDigits || "0").padStart(
		FUEL_LITERS_MIN_INTEGER_DIGITS,
		"0"
	);

	return `${integerPart},${decimalsDigits}`;
};

/**
 * Converte valor digitado (string) no formato da máscara para número (ex: "00,001" → 0.001).
 */
export const parseFuelLiters = (value: string): number | undefined => {
	const digits = onlyDigits(value);
	if (!digits) {
		return;
	}

	const num = Number(digits) / 10 ** FUEL_LITERS_DECIMALS;
	return Number.isNaN(num) ? undefined : num;
};

/**
 * Converte número do estado (ex: 12.345) para string exibida no input (ex: "12,345").
 */
export const formatFuelLitersNumber = (
	value: number | null | undefined
): string => {
	if (value === undefined || value === null || Number.isNaN(value)) {
		return "";
	}

	// Evita erros de ponto flutuante ao transformar o número para a representação com 3 casas.
	const digits = String(Math.round(value * 10 ** FUEL_LITERS_DECIMALS));
	return maskFuelLiters(digits);
};
